import React, { useEffect, useRef, useState } from 'react'

import Dialog, { DialogBody, DialogContent, DialogHeader } from '../../Dialog'
import { probeRelay } from './relayDirectory'
import { normalizeRelayHost } from './relayDirectoryParse'
import styles from './styles.module.scss'

import type { DialogProps } from '../../../contexts/DialogContext'
import type { RelayProbe } from './relayDirectory'

// English is hardcoded here (like the rest of patch desktop/0042) so the relay
// picker stays a self-contained patch and doesn't have to touch every locale
// file. See PATCHES.md.

type Props = {
  /** the pickable relays, default relay first (from the directory) */
  relays: string[]
  /** the instance's default relay — always selectable, tagged, never "down" */
  defaultHost: string
  /** the relay currently in effect (a directory relay, the default, or a
   * previously-typed custom host); '' / undefined means nothing is selected
   * (no checkmark) — used when picking a relay to ADD rather than to switch to */
  selectedHost?: string
  /** hosts filtered out of both the directory list and the contact section
   * (e.g. relays already configured as transports) */
  excludeHosts?: string[]
  /** a second "Relays your contacts use" section below the directory list;
   * NOT probed on open (probing would broadcast contact-derived hostnames to
   * the bridge operator), only when the user clicks "Measure ping" */
  contactRelays?: { host: string; contacts: number }[]
  /** commit a choice; `undefined` means "use the default" */
  onSelect: (host?: string) => void
}

/** A round-trip time bucket, just for the colour of the signal dot. */
function latencyClass(ms: number): string {
  if (ms < 100) return styles.relayDialogDotGood
  if (ms < 250) return styles.relayDialogDotOkay
  return styles.relayDialogDotSlow
}

/**
 * slothfulchat-web: the "choose a chatmail relay" dialog opened from the
 * instant-onboarding relay picker. A dialog (rather than an inline dropdown,
 * which clipped against the onboarding dialog's edge) gives the relay list room
 * to show live per-relay state: a spinner while probing, a round-trip latency
 * when the relay answers on its IMAPS port over the bridge, or greyed-out and
 * unselectable when the bridge can't reach it. Relays that resolve but can't be
 * measured (an allowlisted hosted bridge blocks the probe) stay selectable but
 * are shown as "unknown", never as a false success. An "Other…" field accepts
 * any chatmail relay by hostname.
 */
export default function RelayPickerDialog({
  relays,
  defaultHost,
  selectedHost,
  excludeHosts,
  contactRelays,
  onSelect,
  onClose,
}: Props & DialogProps) {
  const [probes, setProbes] = useState<Record<string, RelayProbe>>({})
  const [customOpen, setCustomOpen] = useState(false)
  const [customInput, setCustomInput] = useState('')
  const [customError, setCustomError] = useState(false)

  const mountedRef = useRef(true)
  const probedRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const excluded = useRef(new Set(excludeHosts ?? [])).current
  const directoryHosts = relays.filter(host => !excluded.has(host))

  // A previously-chosen custom host isn't in the directory; show it too.
  const hosts =
    selectedHost && selectedHost !== defaultHost
      ? directoryHosts.includes(selectedHost)
        ? directoryHosts
        : [...directoryHosts, selectedHost]
      : directoryHosts

  // The contact section: ordered independently (initial order comes from the
  // caller — contacts desc, then alpha), re-sorted only when "Measure ping"
  // finishes. Excluded hosts are dropped here too.
  const [contactOrder, setContactOrder] = useState(() =>
    (contactRelays ?? []).filter(c => !excluded.has(c.host))
  )
  const [measuring, setMeasuring] = useState(false)

  // Opening the dialog IS the intent to choose, so probe every relay now — but
  // through a small worker pool, not all at once: each probe opens a WebSocket
  // to the bridge (a /dns then a /tcp), so firing a whole directory in parallel
  // would flood a single-process local bridge (and could starve the runtime's
  // own bridge health check). A handful in flight fills the list fast enough.
  useEffect(() => {
    let cancelled = false
    const queue = hosts.filter(host => !probedRef.current.has(host))
    queue.forEach(host => probedRef.current.add(host))
    const CONCURRENCY = 5
    let next = 0
    const worker = async (): Promise<void> => {
      while (!cancelled && next < queue.length) {
        const host = queue[next++]
        const probe = await probeRelay(host)
        if (!cancelled && mountedRef.current) {
          setProbes(prev => ({ ...prev, [host]: probe }))
        }
      }
    }
    for (let i = 0; i < Math.min(CONCURRENCY, queue.length); i++) {
      void worker()
    }
    return () => {
      cancelled = true
    }
    // hosts is derived from immutable props; probe once on open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isSelectable = (host: string) =>
    host === defaultHost || probes[host]?.status !== 'unreachable'

  const choose = (host: string) => {
    if (!isSelectable(host)) {
      return
    }
    onSelect(host === defaultHost ? undefined : host)
    onClose()
  }

  const submitCustom = () => {
    const host = normalizeRelayHost(customInput)
    if (!host) {
      setCustomError(true)
      return
    }
    onSelect(host === defaultHost ? undefined : host)
    onClose()
  }

  // Probe the contact section on demand only. Privacy: probing broadcasts
  // contact-derived hostnames to the bridge operator, so nothing hits the
  // network until the user clicks. Same probeRelay + worker pool as the
  // directory probe above; when ALL settle, re-sort by latency asc (unreachable
  // last). Re-sort happens only here, never spontaneously while idle.
  const measureContacts = () => {
    if (measuring || contactOrder.length === 0) {
      return
    }
    setMeasuring(true)
    const queue = contactOrder.map(c => c.host)
    queue.forEach(host => probedRef.current.add(host))
    const results: Record<string, RelayProbe> = {}
    const CONCURRENCY = 5
    let next = 0
    let settled = 0
    const worker = async (): Promise<void> => {
      // Stop probing the remaining hosts if the dialog is closed mid-measure —
      // mirrors the directory probe's cancellation (mountedRef flips on unmount).
      while (mountedRef.current && next < queue.length) {
        const host = queue[next++]
        const probe = await probeRelay(host)
        results[host] = probe
        if (mountedRef.current) {
          setProbes(prev => ({ ...prev, [host]: probe }))
        }
        if (++settled === queue.length && mountedRef.current) {
          const rank = (host: string) => {
            const p = results[host]
            return p && p.status === 'reachable' && p.latencyMs !== null
              ? p.latencyMs
              : Number.POSITIVE_INFINITY
          }
          setContactOrder(prev =>
            [...prev].sort(
              (a, b) =>
                rank(a.host) - rank(b.host) || a.host.localeCompare(b.host)
            )
          )
          setMeasuring(false)
        }
      }
    }
    for (let i = 0; i < Math.min(CONCURRENCY, queue.length); i++) {
      void worker()
    }
  }

  const renderStatus = (host: string, pending: boolean) => {
    const probe = probes[host]
    if (!probe) {
      if (!pending) {
        return null
      }
      // sonar ping while this relay is being probed over the bridge
      return (
        <span className={styles.relayDialogPing} aria-hidden='true'>
          <span className={styles.relayDialogPingRing} />
          <span className={styles.relayDialogPingRing} />
        </span>
      )
    }
    if (probe.status === 'unreachable' || probe.latencyMs === null) {
      return <span className={styles.relayDialogUnreachable}>unreachable</span>
    }
    return (
      <span className={styles.relayDialogLatency}>
        <span
          className={`${styles.relayDialogDot} ${latencyClass(probe.latencyMs)}`}
          aria-hidden='true'
        />
        {probe.latencyMs} ms
      </span>
    )
  }

  return (
    <Dialog onClose={onClose} width={400}>
      <DialogHeader title='Choose a chatmail relay' onClose={onClose} />
      <DialogBody>
        <DialogContent>
          <div
            className={styles.relayDialogList}
            role='listbox'
            aria-label='Public relay directory'
          >
            {hosts.map(host => (
              <button
                type='button'
                key={host}
                role='option'
                aria-selected={host === selectedHost}
                aria-disabled={!isSelectable(host)}
                data-testid={`relay-option-${host}`}
                disabled={!isSelectable(host)}
                className={[
                  styles.relayDialogRow,
                  host === selectedHost ? styles.relayDialogRowSelected : '',
                  !isSelectable(host) ? styles.relayDialogRowDisabled : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => choose(host)}
              >
                <span className={styles.relayDialogHost}>
                  {host === selectedHost && (
                    <span className={styles.relayDialogCheck} aria-hidden='true'>
                      ✓{' '}
                    </span>
                  )}
                  {host}
                  {host === defaultHost && (
                    <span className={styles.relayDialogDefaultTag}>default</span>
                  )}
                </span>
                {renderStatus(host, true)}
              </button>
            ))}
          </div>

          {contactOrder.length > 0 && (
            <div className={styles.relayContactSection}>
              <div className={styles.relayContactHeader}>
                <span className={styles.relayContactTitle}>
                  Relays your contacts use
                </span>
                <button
                  type='button'
                  data-testid='relay-contact-measure'
                  className={styles.relayContactMeasure}
                  disabled={measuring}
                  onClick={measureContacts}
                >
                  Measure ping
                </button>
              </div>
              <p className={styles.relayContactHint}>
                These relays are only pinged (via your bridge) when you press
                “Measure ping”.
              </p>
              <div
                className={styles.relayDialogList}
                role='listbox'
                aria-label='Relays your contacts use'
              >
                {contactOrder.map(({ host, contacts }) => (
                  <button
                    type='button'
                    key={host}
                    role='option'
                    aria-selected={host === selectedHost}
                    aria-disabled={!isSelectable(host)}
                    data-testid={`relay-option-${host}`}
                    disabled={!isSelectable(host)}
                    className={[
                      styles.relayDialogRow,
                      host === selectedHost
                        ? styles.relayDialogRowSelected
                        : '',
                      !isSelectable(host) ? styles.relayDialogRowDisabled : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => choose(host)}
                  >
                    <span className={styles.relayDialogHost}>
                      {host === selectedHost && (
                        <span
                          className={styles.relayDialogCheck}
                          aria-hidden='true'
                        >
                          ✓{' '}
                        </span>
                      )}
                      {host}
                      <span className={styles.relayDialogDefaultTag}>
                        {contacts} contact{contacts === 1 ? '' : 's'}
                      </span>
                    </span>
                    {renderStatus(host, measuring)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!customOpen ? (
            <button
              type='button'
              className={styles.relayDialogOther}
              data-testid='relay-option-other'
              onClick={() => setCustomOpen(true)}
            >
              Other relay…
            </button>
          ) : (
            <div className={styles.relayDialogCustomRow}>
              <input
                type='text'
                autoFocus={true}
                data-testid='relay-custom-input'
                className={[
                  styles.relayDialogCustomInput,
                  customError ? styles.relayDialogCustomInputError : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                placeholder='relay hostname, e.g. chat.example.org'
                value={customInput}
                onChange={event => {
                  setCustomInput(event.target.value)
                  if (customError) {
                    setCustomError(false)
                  }
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    submitCustom()
                  }
                }}
              />
              <button
                type='button'
                data-testid='relay-custom-confirm'
                className={styles.relayDialogCustomConfirm}
                onClick={submitCustom}
              >
                Use
              </button>
            </div>
          )}
        </DialogContent>
      </DialogBody>
    </Dialog>
  )
}
